/**
 * The company and franchise admin panels (spec `docs/superpowers/specs/2026-10-27-company-and-
 * franchise-admin-design.md §5`). UI-facing types; every id is a `string` (backend public UUIDs),
 * every enum keeps its server `label` alongside the code.
 */

/** Always all four, in this fixed order — the backend returns them pre-sorted the same way. */
export const SWITCH_CAPABILITIES = [
  'REFUND',
  'PAYMENT_PLAN',
  'INVITE_STAFF',
  'INVITE_MANAGER',
] as const
export type SwitchCapability = (typeof SWITCH_CAPABILITIES)[number]

export type SwitchLevel = 'franchise' | 'location'

/**
 * One capability's state at one node. `allowed` is this level's own switch;
 * `allowedAbove` is whether the franchise allows it (always `true` at franchise
 * level). Effective = `allowed && allowedAbove`. `changedBy`/`changedAt` are
 * `null` when the switch has never been changed.
 */
export interface CapabilitySwitch {
  capability: string
  label: string
  description: string | null
  allowed: boolean
  allowedAbove: boolean
  changedBy: string | null
  changedAt: string | null
}

export interface OrgLocation {
  publicId: string
  name: string
  timezone: string
  staffCount: number
}

export interface OrgFranchise {
  publicId: string
  name: string
  kind: string
  kindLabel: string
  partners: { publicId: string; displayName: string }[]
  locations: OrgLocation[]
}

/** `GET /api/organization` — only the nodes inside the caller's scope. */
export interface Organization {
  company: { publicId: string; name: string }
  franchises: OrgFranchise[]
}

/** Where an assignment, invitation or switch lives in the tree. */
export interface NodeRef {
  level: 'company' | 'franchise' | 'location'
  publicId: string
  name: string
}

export interface StaffMember {
  publicId: string
  displayName: string
  email: string
  status: string
  statusLabel: string
  lastLoginAt: string | null
  assignments: { role: { key: string; label: string }; node: NodeRef }[]
  /** The server's answer to "may this caller block this user" — false for self and superiors. */
  canManage: boolean
}

export interface PendingInvitation {
  publicId: string
  email: string
  fullName: string
  role: { key: string; label: string }
  node: NodeRef
  invitedBy: string | null
  createdAt: string
  expiresAt: string
}

export interface PriceRequest {
  id: string
  franchise: string
  status: string
  statusLabel: string
  kind: string
  kindLabel: string
  creditQty: number
  priceGross: number
  currency: string
  rateCount: number | null
  termPeriods: number | null
  note: string | null
  requestedBy: string | null
  createdAt: string | null
  decidedBy: string | null
  decidedAt: string | null
  decisionNote: string | null
  productId: string | null
}

/**
 * What the franchise partner's "Neue Preisanfrage" form sends, before
 * `toPriceRequestEntry` (Task 2's `priceRequestsApi.ts`) adds `currency` and
 * nulls `termPeriods` for anything but a subscription. The create schema
 * (Task 6) infers this same shape.
 */
export interface PriceRequestInput {
  kind: string
  creditQty: number
  priceGross: number
  rateCount: number
  termPeriods: number | null
  note: string
}

/**
 * Which node a staff/invitations query is scoped to — structural so
 * `queryKeys.ts` (in `lib/`) can key on it without importing this feature.
 */
export type AdminScope = { franchise: string } | { location: string } | Record<string, never>
